import { useCallback, useEffect, useState } from "react";
import { IColumn } from "../../components/UI/Declarations";
import { IColumnOption } from "../../components/UI/SelectColumnModal";
import { useLocalStorage } from "../../components/UI/LocalStorage";

interface UsePagedListColumnsProps {
    columns: IColumn[];
    persistanceKey?: string;
}

export const usePagedListColumns = ({ columns, persistanceKey }: UsePagedListColumnsProps) => {
    const [allColumns, setAllColumns] = useState(columns);
    const [visibleColumns, setVisibleColumns] = useState<IColumn[]>(
        columns.filter((column) => column.defaultVisible !== false)
    );
    const [tableWidth, setTableWidth] = useState<number>(
        visibleColumns.reduce((sum, column) => (sum += column.width), 0)
    );
    const [showSelectColumnsModal, setShowSelectColumnsModal] = useState(false);
    const [columnOptions, setColumnOptions] = useState<IColumnOption[]>([]);

    const [storedColumns, setStoredColumns] = useLocalStorage<IColumnOption[]>(
        persistanceKey + "-columns",
        null
    );

    const toFindDuplicateColumns = useCallback((columns: IColumn[]): string[] => {
        const duplicates: string[] = [];
        const seen = new Set<string>();

        columns.forEach((column) => {
            if (seen.has(column.member)) {
                duplicates.push(column.member);
            } else {
                seen.add(column.member);
            }
        });

        return duplicates;
    }, []);

    const handleSelectColumnsUpdated = useCallback((columnOptions: IColumnOption[]): void => {
        function sanitizeColumnOptions(columnOptions: IColumnOption[]): IColumnOption[] {
            const duplicates = toFindDuplicateColumns(allColumns);
            const sanitizedOptions: IColumnOption[] = [];

            columnOptions.forEach((option) => {
                if (duplicates.includes(option.member)) {
                    const existingOption = sanitizedOptions.find((o) => o.member === option.member);
                    if (existingOption) {
                        existingOption.isVisible = existingOption.isVisible || option.isVisible;
                    } else {
                        sanitizedOptions.push(option);
                    }
                } else {
                    sanitizedOptions.push(option);
                }
            });

            return sanitizedOptions;
        }

        const sanitizedOptions = sanitizeColumnOptions(columnOptions);
        setColumnOptions(sanitizedOptions);
        setStoredColumns(sanitizedOptions);

        // Reorder allColumns based on the new columnOptions order
        const reorderedColumns: IColumn[] = [];
        sanitizedOptions.forEach((option) => {
            const column = allColumns.find((col) => col.member === option.member);
            if (column) {
                reorderedColumns.push(column);
            }
        });

        // Add any remaining columns that weren't in columnOptions
        allColumns.forEach((column) => {
            if (!sanitizedOptions.find((opt) => opt.member === column.member)) {
                reorderedColumns.push(column);
            }
        });

        setAllColumns(reorderedColumns);

        // Update visible columns based on the reordered columns and visibility settings
        const newVisibleColumns = reorderedColumns.filter((column) => {
            const option = sanitizedOptions.find((o) => o.member === column.member);
            return option?.isVisible !== false;
        });

        setVisibleColumns(newVisibleColumns);
        setTableWidth(newVisibleColumns.reduce((sum, column) => (sum += column.width), 0));
        setShowSelectColumnsModal(false);
    }, [allColumns, setStoredColumns, toFindDuplicateColumns]);

    const columnsToColumnOptions = useCallback((columns: IColumn[]): IColumnOption[] => {
        return columns.map((column) => ({
            member: column.member,
            title: column.title,
            isVisible: column.defaultVisible !== false,
        }));
    }, []);

    const handleSelectColumnsClicked = useCallback((): void => {
        const currentOptions = allColumns.map((column) => ({
            member: column.member,
            title: column.title,
            isVisible: visibleColumns.some((visibleCol) => visibleCol.member === column.member),
        }));
        setColumnOptions(currentOptions);
        setShowSelectColumnsModal(true);
    }, [allColumns, visibleColumns]);

    const handleSelectColumnsReset = useCallback((): void => {
        const defaultOptions = columnsToColumnOptions(columns);
        setColumnOptions(defaultOptions);
        setStoredColumns(defaultOptions);
        setAllColumns(columns);
        setVisibleColumns(columns.filter((column) => column.defaultVisible !== false));
        setTableWidth(columns.filter((column) => column.defaultVisible !== false).reduce((sum, column) => (sum += column.width), 0));
    }, [columns, columnsToColumnOptions, setStoredColumns]);

    // Initialize column order/visibility from localStorage once (do not depend on allColumns - updating allColumns re-triggers this effect).
    useEffect(() => {
        if (!storedColumns) return;

        const reorderedColumns: IColumn[] = [];
        storedColumns.forEach((storedOption) => {
            const column = columns.find((col) => col.member === storedOption.member);
            if (column) {
                reorderedColumns.push(column);
            }
        });

        columns.forEach((column) => {
            if (!storedColumns.find((stored) => stored.member === column.member)) {
                reorderedColumns.push(column);
            }
        });

        setAllColumns(reorderedColumns);

        const newVisibleColumns = reorderedColumns.filter((column) => {
            const option = storedColumns.find((o) => o.member === column.member);
            return option?.isVisible !== false;
        });
        setVisibleColumns(newVisibleColumns);
        setTableWidth(newVisibleColumns.reduce((sum, column) => (sum += column.width), 0));
    }, [storedColumns, columns]);

    // Update table width when visible columns change
    useEffect(() => {
        setTableWidth(visibleColumns.reduce((sum, column) => (sum += column.width), 0));
    }, [visibleColumns]);

    return {
        allColumns,
        setAllColumns,
        visibleColumns,
        setVisibleColumns,
        tableWidth,
        setTableWidth,
        showSelectColumnsModal,
        setShowSelectColumnsModal,
        columnOptions,
        setColumnOptions,
        handleSelectColumnsUpdated,
        handleSelectColumnsClicked,
        handleSelectColumnsReset
    };
};
